import { createFileRoute, Outlet } from '@tanstack/react-router';
import type { ReactElement } from 'react';
import { NotFoundScreen } from '@/common/ErrorScreen';
import { useCurrentUser } from '~/hooks/useCurrentUser';

/**
 * The All Partners shell: the coordinator's partner directory and the Add
 * Partner form.
 *
 * A top-level route, not a child of `/follow-ups`, whose shell would paint its
 * two-tab strip with neither tab active. It has a shell because it has two
 * children.
 *
 * Coordinators only: unlike the follow-up network, admins are refused, as
 * legacy's tab refused them and the endpoints do. Defence in depth, never the
 * only gate.
 */
function FollowUpPartnersLayout(): ReactElement | null {
  const { me, isPending } = useCurrentUser();

  // `me.profile_type`: `useCurrentUser().isStudyHelper` includes every
  // non-student type, and `useIsCoordinator` needs a group slug, which a
  // top-level route doesn't have.
  const allowed = me?.profile_type === 'FOLLOWUP_COORDINATOR';

  // `/users/me` is pending on first paint and a bare `!allowed` bounces every
  // caller, the coordinator included.
  if (isPending) return null;
  if (!allowed) return <NotFoundScreen />;

  // No title here: the two children have different headings, as in legacy.
  return <Outlet />;
}

export const Route = createFileRoute('/_app/follow-up-partners')({
  component: FollowUpPartnersLayout,
});
